import { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Mousewheel } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

export default function RoughCountrySlider({ data }) {
    const fallbackImage = "https://media.roughcountry.com/media/catalog/product/r/c/rc-logo-stamp2.jpg";

    const images = [
        data.image_1,
        data.image_2,
        data.image_3,
        data.image_4,
        data.image_5,
        data.image_6,
    ].filter(Boolean);

    const finalImages = images.length > 0 ? images : [fallbackImage];
    const videoUrl = data.video_url || null;

    // Helper to safely extract YouTube Video ID
    const extractVideoId = (url) => {
        if (!url) return null;
        const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
        return match ? match[1] : null;
    };

    const videoId = extractVideoId(videoUrl);
    const embedVideoUrl = videoId ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1` : null;

    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const mediaItems = [
        ...finalImages.map((img) => ({ type: "image", src: img })),
        ...(embedVideoUrl ? [{ type: "video", src: embedVideoUrl, videoId }] : []),
    ];

    const [imagesNavSlider, setImagesNavSlider] = useState(null);
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    return (
        <section className="slider">
            <div className="slider__flex">
                <div className="slider__col">
                    {/* Thumbnails */}
                    <div className="slider__thumbs">
                        <Swiper
                            onSwiper={setImagesNavSlider}
                            direction="vertical"
                            spaceBetween={6}
                            slidesPerView={6}
                            breakpoints={{
                                0: { direction: "horizontal" },
                                768: { direction: "vertical" },
                            }}
                            modules={[Navigation, Thumbs]}
                        >
                            {mediaItems.map((item, index) => (
                                <SwiperSlide key={index}>
                                    <div
                                        className="slider__image"
                                        onMouseEnter={() => setActiveImageIndex(index)}
                                    >
                                        {item.type === "image" ? (
                                            <img
                                                src={item.src}
                                                alt={`Thumb ${index}`}
                                                width="100%"
                                                height="100%"
                                            />
                                        ) : (
                                            <div style={{ position: "relative" }}>
                                                <img
                                                    src={`https://img.youtube.com/vi/${item.videoId}/0.jpg`}
                                                    alt="Video Thumbnail"
                                                    width="100%"
                                                    height="100%"
                                                />
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        fontSize: "20px",
                                                        color: "white",
                                                    }}
                                                >
                                                    ▶
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>

                {/* Main Slider */}
                <div className="slider__images mt-2">
                    <Swiper
                        thumbs={{
                            swiper:
                                imagesNavSlider && !imagesNavSlider.destroyed
                                    ? imagesNavSlider
                                    : null,
                        }}
                        slidesPerView={1}
                        spaceBetween={15}
                        navigation={{
                            nextEl: ".slider__next",
                            prevEl: ".slider__prev",
                        }}
                        modules={[Navigation, Thumbs, Mousewheel]}
                        onSlideChange={(swiper) => {
                            // Reset video play overlay when changing slides
                            setIsPlaying(false);

                            const previousIndex = swiper.previousIndex;
                            const previousItem = mediaItems[previousIndex];

                            if (previousItem?.type === "video" && videoRef.current) {
                                videoRef.current.contentWindow.postMessage(
                                    '{"event":"command","func":"pauseVideo","args":""}',
                                    "*"
                                );
                            }
                            setActiveImageIndex(swiper.activeIndex);
                        }}
                    >
                        {mediaItems.map((item, index) => (
                            <SwiperSlide key={index}>
                                <div className="slider__image">
                                    {item.type === "image" ? (
                                        <img
                                            src={item.src}
                                            alt={`Main ${index}`}
                                            width="100%"
                                            height="100%"
                                        />
                                    ) : (
                                        <div style={{ position: "relative", width: "100%", height: "400px" }}>
                                            {/* Poster Overlay to handle touch gestures */}
                                            {!isPlaying && (
                                                <div
                                                    onClick={() => setIsPlaying(true)}
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        zIndex: 2,
                                                        cursor: "pointer",
                                                        background: "#000",
                                                    }}
                                                >
                                                    <img
                                                        src={`https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`}
                                                        alt="Video Poster"
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            top: "50%",
                                                            left: "50%",
                                                            transform: "translate(-50%, -50%)",
                                                            width: "60px",
                                                            height: "60px",
                                                            borderRadius: "50%",
                                                            backgroundColor: "rgba(0,0,0,0.7)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: "#fff",
                                                            fontSize: "24px",
                                                        }}
                                                    >
                                                        ▶
                                                    </div>
                                                </div>
                                            )}
                                            {/* YouTube Iframe */}
                                            <iframe
                                                ref={videoRef}
                                                width="100%"
                                                height="400"
                                                src={isPlaying ? `${item.src}&autoplay=1` : item.src}
                                                title="Product Video"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            />
                                        </div>
                                    )}
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </section>
    );
}














// import { useState, useRef } from "react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Navigation, Thumbs, Mousewheel } from "swiper";
// import "swiper/css";
// import "swiper/css/navigation";

// export default function RoughCountrySlider({ data }) {

//     // const images = [
//     //     data.image_1 || null,
//     //     data.image_2 || null,
//     //     data.image_3 || null,
//     //     data.image_4 || null,
//     //     data.image_5 || null,
//     //     data.image_6 || null
//     // ].filter(img => img !== null);

//     const fallbackImage = "https://media.roughcountry.com/media/catalog/product/r/c/rc-logo-stamp2.jpg";

//     const images = [
//         data.image_1,
//         data.image_2,
//         data.image_3,
//         data.image_4,
//         data.image_5,
//         data.image_6,
//     ].filter(Boolean);

//     // If no images exist, use the logo as the only image
//     const finalImages = images.length > 0 ? images : [fallbackImage];

//     const videoUrl = data.video_url || null;

//     const getEmbedUrl = (url) => {
//         if (!url) return null;
//         const videoId = url.split("v=")[1]?.split("&")[0];
//         return videoId
//             ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1`
//             : null;
//     };

//     const videoRef = useRef(null);
//     const embedVideoUrl = getEmbedUrl(videoUrl);

//     const mediaItems = [
//         ...finalImages.map(img => ({ type: "image", src: img })),
//         ...(embedVideoUrl ? [{ type: "video", src: embedVideoUrl }] : [])
//     ];

//     const [imagesNavSlider, setImagesNavSlider] = useState(null);
//     const [activeImageIndex, setActiveImageIndex] = useState(0);

//     return (
//         <section className="slider">
//             <div className="slider__flex">
//                 <div className="slider__col">

//                     {/* Thumbnails */}
//                     <div className="slider__thumbs">
//                         <Swiper
//                             onSwiper={setImagesNavSlider}
//                             direction="vertical"
//                             spaceBetween={6}
//                             slidesPerView={6}
//                             breakpoints={{
//                                 0: { direction: "horizontal" },
//                                 768: { direction: "vertical" }
//                             }}
//                             modules={[Navigation, Thumbs]}
//                         >
//                             {mediaItems.map((item, index) => (
//                                 <SwiperSlide key={index}>
//                                     <div
//                                         className="slider__image"
//                                         onMouseEnter={() => setActiveImageIndex(index)}
//                                     >
//                                         {item.type === "image" ? (
//                                             <img
//                                                 src={item.src}
//                                                 alt={`Thumb ${index}`}
//                                                 width="100%"
//                                                 height="100%"
//                                             />
//                                         ) : (
//                                             <div style={{ position: "relative" }}>
//                                                 <img
//                                                     src={`https://img.youtube.com/vi/${videoUrl.split("v=")[1]}/0.jpg`}
//                                                     alt="Video Thumbnail"
//                                                     width="100%"
//                                                     height="100%"
//                                                 />
//                                                 <div style={{
//                                                     position: "absolute",
//                                                     top: "50%",
//                                                     left: "50%",
//                                                     transform: "translate(-50%, -50%)",
//                                                     fontSize: "20px",
//                                                     color: "white"
//                                                 }}>
//                                                     ▶
//                                                 </div>
//                                             </div>
//                                         )}
//                                     </div>
//                                 </SwiperSlide>
//                             ))}
//                         </Swiper>
//                     </div>
//                 </div>

//                 {/* Main Slider */}
//                 <div className="slider__images mt-2">
//                     <Swiper
//                         thumbs={{
//                             swiper: imagesNavSlider && !imagesNavSlider.destroyed
//                                 ? imagesNavSlider
//                                 : null
//                         }}
//                         slidesPerView={1}
//                         spaceBetween={15}
//                         navigation={{
//                             nextEl: ".slider__next",
//                             prevEl: ".slider__prev"
//                         }}
//                         modules={[Navigation, Thumbs, Mousewheel]}
//                         onSlideChange={(swiper) => {

//                             const previousIndex = swiper.previousIndex;
//                             const previousItem = mediaItems[previousIndex];

//                             if (previousItem?.type === "video" && videoRef.current) {
//                                 videoRef.current.contentWindow.postMessage(
//                                     '{"event":"command","func":"pauseVideo","args":""}',
//                                     "*"
//                                 );
//                             }
//                             setActiveImageIndex(swiper.activeIndex);
//                         }}
//                     >
//                         {mediaItems.map((item, index) => (
//                             <SwiperSlide key={index}>
//                                 <div className="slider__image">
//                                     {item.type === "image" ? (
//                                         <img
//                                             src={item.src}
//                                             alt={`Main ${index}`}
//                                             width="100%"
//                                             height="100%"
//                                         />
//                                     ) : (
//                                         <iframe
//                                             ref={videoRef}
//                                             width="100%"
//                                             height="400"
//                                             src={item.src}
//                                             title="Product Video"
//                                             frameBorder="0"
//                                             allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
//                                             allowFullScreen
//                                         />
//                                     )}
//                                 </div>
//                             </SwiperSlide>
//                         ))}
//                     </Swiper>
//                 </div>
//             </div>
//         </section>
//     );
// }
